import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as auth from "@script/auth";
import useStore from "@store/store";
import { useEffect } from "react";
import { QUERY_BASE_KEY } from "@constants/query-key.ts";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import httpClient from "@/config/axios";

interface PersonnelBasicInfo {
  profile_image_url?: string;
  gender?: string;
}

export function useSession() {
  const { isAuthenticated, setLoginUser, setPersonnelBasicInfo, logout } =
    useStore();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: [QUERY_BASE_KEY.SESSION],
    queryFn: async () => {
      try {
        const user = await auth.loadLoginUser();
        return user;
      } catch (error) {
        // 에러 발생 시 null 반환하여 아래 useEffect에서 로그아웃 처리 유도
        console.error("Session load failed:", error);
        return null;
      }
    },
    retry: 0,
    staleTime: 1000 * 60 * 30, // 30분
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated, // 로그인 상태일 때만 요청
  });

  // personnel-basic-info도 함께 가져오기
  const { data: personnelData } = useQuery({
    queryKey: QUERY_KEYS.nurses.personnelBasicInfo(),
    queryFn: async () => {
      try {
        return await httpClient.get<PersonnelBasicInfo>(
          "/nurses/personnel-basic-info",
          {
            withCredentials: true,
          }
        );
      } catch (error) {
        console.error("Personnel basic info load failed:", error);
        return null;
      }
    },
    retry: 0,
    staleTime: 1000 * 60 * 30, // 30분
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated, // 로그인 상태일 때만 요청
  });

  useEffect(() => {
    // 1. 로그인이 안 된 상태면 아무것도 안 함
    if (!isAuthenticated) return;

    // 2. 로딩 중이면 대기
    if (isLoading) return;

    // 3. 결과 처리
    if (isSuccess && data) {
      setLoginUser(data);
    } else if (isError || (isSuccess && !data)) {
      // 에러가 났거나, 성공했는데 데이터가 없으면(null) 로그아웃
      queryClient.clear();
      logout();
    }
  }, [
    isLoading,
    isSuccess,
    isError,
    data,
    isAuthenticated,
    setLoginUser,
    logout,
    queryClient,
  ]);

  // personnel-basic-info 저장
  useEffect(() => {
    if (personnelData) {
      setPersonnelBasicInfo(
        personnelData.profile_image_url || null,
        personnelData.gender || null
      );
    }
  }, [personnelData, setPersonnelBasicInfo]);

  return { isLoading };
}
